import { v4 } from 'uuid';

import { api } from 'utils';
import { getApiDomain } from 'utils/url';

import { version } from '../../../package.json';
import localForage from '../localForage';

export interface RegisterDeviceParams {
  kioskCode: string;
  fp: string;
}

export interface OTPDeviceParams {
  locationId: string;
  fp: string;
  otp: string;
}

export interface ChallengeParams {
  locationId: string;
  fp: string;
  value: string;
}

export interface RegisterDeviceResponse {
  locationId: string;
  locationName: string;
  businessName: string;
  areaName?: string;
  email: string;
}

export interface OTPDeviceResponse {
  businessName: string;
  locationName: string;
  areaName: string;
  locationId: string;
  email: string;
}

export interface ChallengeResponse {
  success: boolean;
}

interface CheckInData {
  contactNumber: string;
  firstName: string;
  lastName: string;
  timestamp: string;
  eventID: string;
}

export interface CheckInParams {
  locationId: string;
  fp: string;
  checkIns: CheckInData[];
  version: string;
}

export interface CheckInResponse {
  success: string;
  challenge?: string;
  v?: number;
  reloadRequired?: boolean;
}

// Reverse proxy requests to the API expoint when developing locally.
// CHeck ./src/setupProxy.js for details.
const API_DOMAIN_ENDPOINT = process.env.NODE_ENV !== 'production' ? '/prod' : getApiDomain();
export const FINGERPRINT_KEY = `fingerprint-uuid-${process.env.REACT_APP_STAGE}`;

export default class DvrsService {
  private adapter: api.Adapter;
  private fingerprintPromise: Promise<string>;

  constructor() {
    this.adapter = new api.Adapter({
      baseURL: API_DOMAIN_ENDPOINT,
      responseType: 'json',
      headers: {
        timeout: 60 * 10 * 1000,
        'content-type': 'application/json',
        accept: 'application/json'
      }
    });

    this.fingerprintPromise = localForage.getItem<string>(FINGERPRINT_KEY).then(value => {
      if (value) {
        return value;
      }

      const fingerprint = v4();
      return localForage.setItem(FINGERPRINT_KEY, fingerprint).then(() => fingerprint);
    });
  }

  async getDeviceFP(): Promise<string> {
    return await this.fingerprintPromise;
  }

  async registerDevice({
    kioskCode
  }: Omit<RegisterDeviceParams, 'fp'>): Promise<RegisterDeviceResponse> {
    const { data } = await this.adapter.postValue<RegisterDeviceParams, RegisterDeviceResponse>(
      '/device/register',
      {
        kioskCode,
        fp: await this.fingerprintPromise
      }
    );

    return data;
  }

  async authenticateDeviceWithOTP(params: Omit<OTPDeviceParams, 'fp'>): Promise<OTPDeviceResponse> {
    const { data } = await this.adapter.postValue<OTPDeviceParams, OTPDeviceResponse>(
      '/device/otp',
      {
        ...params,
        otp: params.otp.toUpperCase(),
        fp: await this.fingerprintPromise
      },
      {
        withCredentials: true
      }
    );

    return data;
  }

  async checkIn(params: Omit<CheckInParams, 'fp' | 'version'>): Promise<CheckInResponse> {
    const { data } = await this.adapter.postValue<CheckInParams, CheckInResponse>(
      '/device/checkin',
      {
        ...params,
        version,
        fp: await this.fingerprintPromise
      },
      {
        withCredentials: true
      }
    );
    return data;
  }

  async challenge(params: Omit<ChallengeParams, 'fp'>): Promise<ChallengeResponse> {
    const { data } = await this.adapter.postValue<ChallengeParams, ChallengeResponse>(
      '/device/challenge',
      {
        ...params,
        fp: await this.fingerprintPromise
      },
      {
        withCredentials: true
      }
    );
    return data;
  }
}
